// The notification center (APP_SHELL.md, NOTIFICATIONS.md §2; K4): what the
// site has told me, newest first. Tap one and land on what it is about, and
// it is read — here and on every other device. PLAN-2026-09-26-bell B2.
//
// Clean (LOOKS.md Part 7): drawn through the kit, so a look restyles it. The
// drawer around it is the shell's (`notificationDrawer.tsx`).

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KIND_TITLES, NOTIFICATION_KINDS, type NotificationKindId } from '@fsp/domain';
import { useNotifications, whenLabel, type Notification } from '../data/notifications';
import { pushState, turnOff, turnOn, type PushState } from '../data/push';
import { useSession } from '../data/session';
import {
  Button,
  Callout,
  Caption,
  Chips,
  Dot,
  Line,
  ListRow,
  RowName,
  RowSub,
  Stack,
  Text,
} from '../kit/ui';

/** Minute by minute, so "Just now" does not stay just now. */
function useMinute(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(t);
  }, []);
  return now;
}

export function NotificationCenter() {
  const center = useNotifications();
  const navigate = useNavigate();
  const now = useMinute();
  // The bell's count too: the unread ones can be past the page that is loaded.
  const anyUnread = (center.unread ?? 0) > 0 || center.items.some((n) => n.readAt === null);

  return (
    <div className="fsp-center">
      <div className="fsp-center-head">
        <Line gap={2} spread>
          <Button
            variant="quiet"
            size="xs"
            icon="accept"
            disabled={!anyUnread}
            onClick={() => void center.markAllRead()}
          >
            Mark all read
          </Button>
          <Button
            variant="quiet"
            size="xs"
            icon="settings"
            onClick={() => {
              center.setOpen(false);
              navigate('/account/notifications');
            }}
          >
            Settings
          </Button>
        </Line>
      </div>
      <div className="fsp-center-list">
        <Stack gap={2}>
          <PushOnThisDevice />
          {/* THE KINDS (DIRECTION A11, B-438): All, with a league's week as one
              row, or one kind's rows as they were written; each with its unread. */}
          <Chips
            label="Show"
            single
            options={[
              { value: 'all', label: 'All' },
              ...NOTIFICATION_KINDS.map((k) => {
                const n = center.unreadByKind[k] ?? 0;
                return { value: k, label: n > 0 ? `${KIND_TITLES[k]} · ${n}` : KIND_TITLES[k] };
              }),
            ]}
            value={[center.kind ?? 'all']}
            onChange={(v) => {
              const next = v[0];
              if (next === undefined) return;
              center.setKind(next === 'all' ? null : (next as NotificationKindId));
            }}
          />
          {center.problem ? <Callout tone="warn">{center.problem}</Callout> : null}
          {center.state === 'loading' && center.items.length === 0 ? (
            <Text tone="muted">Reading your notifications.</Text>
          ) : null}
          {center.state === 'ready' && center.items.length === 0 ? (
            <Text tone="muted">
              {center.kind === null
                ? 'Nothing yet. A trade offer, a waiver result or a draft that needs you lands here.'
                : `Nothing under ${KIND_TITLES[center.kind]} yet.`}
            </Text>
          ) : null}
          <div role="list" aria-label="Notifications">
            {center.items.map((n) => (
              <Row key={n.id} n={n} now={now} onOpen={() => center.land(n)} />
            ))}
          </div>
          {center.more ? (
            <Button variant="secondary" size="xs" onClick={() => void center.loadOlder()}>
              Older
            </Button>
          ) : null}
          {/* The server's rule, said where the list ends (B-265). */}
          {center.kept !== null && center.items.length > 0 && !center.more ? (
            <Text tone="muted">
              Read notifications are kept for {center.kept.readDays} days and unread ones for{' '}
              {center.kept.unreadDays}.
            </Text>
          ) : null}
        </Stack>
      </div>
    </div>
  );
}

function Row({ n, now, onOpen }: { n: Notification; now: number; onOpen: () => void }) {
  const unread = n.readAt === null;
  return (
    <div role="listitem">
      <ListRow lead={<Dot on={unread} />} leadSize="sm" tall onClick={onOpen}>
        <RowName>{n.title}</RowName>
        <RowSub wrap>{n.body}</RowSub>
        <Caption>
          {whenLabel(n.createdAt, now)}
          {unread ? ' · new' : ''}
        </Caption>
      </ListRow>
    </div>
  );
}

/**
 * One line about this device, whichever is true (PLAN-2026-09-26-bell B3):
 * push on, off, blocked, needing the installed app, or not possible here.
 * The words say what will happen before anybody taps (CLAUDE.md, "Say what
 * will happen"), and the prompt comes only from *Turn on*.
 */
export function PushOnThisDevice() {
  const { session } = useSession();
  const navigate = useNavigate();
  const { setOpen } = useNotifications();
  const userId = session?.userId ?? null;
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  useEffect(() => {
    if (userId === null) return;
    let live = true;
    void pushState(userId).then((s) => live && setState(s));
    return () => {
      live = false;
    };
  }, [userId]);
  if (state === null || userId === null) return null;
  const act = async (run: typeof turnOn) => {
    setBusy(true);
    const done = await run(userId);
    setBusy(false);
    if (done.ok) {
      setProblem(null);
      setState(done.state);
    } else setProblem(done.message);
  };
  const words: Record<PushState, string> = {
    on: 'Notifications are on for this device, even with the site closed.',
    off: 'Get notifications on this device too, even with the site closed — the ones set to push in Settings. Your browser will ask first.',
    denied:
      'This browser is set to block notifications from this site. Allow them in its site settings, then open this again.',
    'needs-install':
      'On an iPhone or iPad, notifications need the app on your Home Screen. The app keeps its own sign-in, so you will sign in once more inside it.',
    unsupported:
      'This browser cannot show notifications from a site. Everything is here in the list either way.',
  };
  return (
    <Callout tone={state === 'on' ? 'good' : 'neutral'}>
      <Stack gap={2}>
        <Text>{words[state]}</Text>
        {problem ? <Text tone="bad">{problem}</Text> : null}
        {state === 'off' ? (
          <Line>
            <Button
              variant="primary"
              size="xs"
              icon="notifications"
              disabled={busy}
              onClick={() => void act(turnOn)}
            >
              Turn on
            </Button>
          </Line>
        ) : null}
        {state === 'needs-install' ? (
          <Line>
            <Button
              variant="primary"
              size="xs"
              icon="install"
              onClick={() => {
                setOpen(false);
                navigate('/account/install');
              }}
            >
              How to install
            </Button>
          </Line>
        ) : null}
        {state === 'on' ? (
          <Line>
            <Button variant="secondary" size="xs" disabled={busy} onClick={() => void act(turnOff)}>
              Turn off
            </Button>
          </Line>
        ) : null}
      </Stack>
    </Callout>
  );
}
